import { useState, useMemo, useEffect } from "react";
import { IMAGE_CATEGORY_LABELS, type ImageCategory, type LibraryImage } from "../../pages/platform/utils/imageLibraryStore";
import { fetchPlatformLibraryImages, fetchTenantPlatformLibraryImages } from "../../api/platformLibrary";

interface Props {
  onSelect: (image: LibraryImage) => void;
  onClose: () => void;
  defaultCategory?: ImageCategory;
  title?: string;
  /** 租户后台传入 legacyId 时走 `/api/tenants/{id}/platform-library-images`，否则走平台接口（需平台登录）。 */
  tenantLegacyId?: string;
}

export default function ImageLibraryPicker({ onSelect, onClose, defaultCategory, title, tenantLegacyId }: Props) {
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<ImageCategory | "all">(defaultCategory ?? "all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = tenantLegacyId?.trim()
      ? fetchTenantPlatformLibraryImages(tenantLegacyId.trim())
      : fetchPlatformLibraryImages();
    void load
      .then((rows) => {
        if (!cancelled) {
          setImages(rows);
          setListLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setListLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const filtered = useMemo(() => {
    return images.filter((img) => {
      const matchCat = activeCategory === "all" || img.category === activeCategory;
      const matchSearch = !search.trim() || (img.name ?? "").includes(search.trim());
      return matchCat && matchSearch;
    });
  }, [images, activeCategory, search]);

  const categories: Array<{ key: ImageCategory | "all"; label: string; count: number }> = [
    { key: "all", label: "全部", count: images.length },
    { key: "activity", label: IMAGE_CATEGORY_LABELS.activity, count: images.filter((i) => i.category === "activity").length },
    { key: "vote", label: IMAGE_CATEGORY_LABELS.vote, count: images.filter((i) => i.category === "vote").length },
    { key: "landing", label: IMAGE_CATEGORY_LABELS.landing, count: images.filter((i) => i.category === "landing").length },
    { key: "news", label: IMAGE_CATEGORY_LABELS.news, count: images.filter((i) => i.category === "news").length },
    { key: "general", label: IMAGE_CATEGORY_LABELS.general, count: images.filter((i) => i.category === "general").length },
  ];

  const handleConfirm = () => {
    const found = images.find((img) => img.id === selectedId);
    if (found) {
      onSelect(found);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl w-[780px] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center">
              <i className="ri-image-2-line text-amber-500"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{title ?? "从运营平台资源库选择"}</h3>
              <p className="text-xs text-gray-400">由平台统一管理的公共图片素材</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Category sidebar */}
          <div className="w-40 flex-shrink-0 border-r border-gray-100 py-3 px-2 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-400 px-2 mb-2 uppercase tracking-wider">分类</p>
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs cursor-pointer transition-all mb-0.5 whitespace-nowrap ${
                  activeCategory === cat.key
                    ? "bg-red-600 text-white font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span>{cat.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${activeCategory === cat.key ? "bg-red-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* Image grid */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search */}
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索图片名称..."
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-gray-50"
                />
              </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadError ? (
                <div className="flex flex-col items-center justify-center h-40 text-red-500 text-sm">
                  资源库加载失败，请检查网络或稍后重试
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400 px-4 text-center">
                  <i className="ri-image-line text-3xl text-gray-200 mb-2"></i>
                  <p className="text-sm">
                    {listLoading
                      ? "正在加载…"
                      : images.length === 0
                        ? "资源库暂无图片，请在平台运营端「图片资源库」上传。"
                        : "该分类或搜索下暂无素材，请切换「全部」或清空搜索。"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {filtered.map((img) => {
                    const isSelected = selectedId === img.id;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => setSelectedId(img.id)}
                        className={`relative rounded-xl overflow-hidden cursor-pointer transition-all group ${
                          isSelected ? "ring-2 ring-red-500 ring-offset-1" : "hover:ring-2 hover:ring-gray-300"
                        }`}
                        style={{ aspectRatio: "16/9" }}
                      >
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className={`absolute inset-0 transition-opacity ${isSelected ? "bg-red-600/20" : "bg-black/0 group-hover:bg-black/20"}`} />
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-2">
                          <p className="text-white text-xs font-medium truncate">{img.name}</p>
                          <p className="text-white/50 text-[10px]">
                            {IMAGE_CATEGORY_LABELS[img.category] ?? img.category}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <p className="text-xs text-gray-400">
            {selectedId ? (
              <span className="text-gray-700 font-medium flex items-center gap-1.5">
                <i className="ri-check-circle-line text-red-500"></i>
                已选择：{filtered.find((img) => img.id === selectedId)?.name ?? ""}
              </span>
            ) : (
              `共 ${filtered.length} 张图片，点击选中后确认使用`
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedId}
              className="px-5 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-40 whitespace-nowrap"
            >
              使用此图片
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
